<template>
    <div id="download-xmp">
        <div id="app-layout-right-column__content__title">
            <span>Download XMP Profile</span>
        </div>

        <PlanUpgradeNotificationInSidebar v-if="isCurrentPlanFree" />
        <div v-else class="py-5">
            <label class="mb-2" for="title" theme="--start">XMP Name</label>
            <div class="input-container" name="input-container--name">
                <v-text-field
                    rounded
                    v-model="$v.options.title.$model"
                    @input="hideEnterNameTip()"
                    :disabled="!canEnterName"
                    name="title"
                    ref="name"
                    placeholder="Eg. My XMP #5"
                />
                <tippy theme="info" to="input-container--name" v-if="!canEnterName">XMP name will bear the respective {{ assetType }} name</tippy>
                <tippy theme="--start" to="input-container--name" v-if="tips.requiredName" :visible="true" trigger="manual">Field is required</tippy>
            </div>

            <label class="mb-2" for="group" theme="--start">Group - optional</label>
            <div class="input-container">
                <v-text-field
                    rounded
                    v-model="options.group"
                    name="group"
                    placeholder="Group name"
                />
            </div>

            <div class="buttons-container">
                <button type="button" class="button button--primary" @click="onSubmit" :disabled="exporting">
                    <v-progress-circular v-if="exporting" indeterminate color="var(--color-white)" size="18" width="3"></v-progress-circular>
                    <span v-else class="font-weight-bold">Export</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapGetters, mapState } from "vuex";
import PlanUpgradeNotificationInSidebar from "@/components/app/ui/PlanUpgradeNotificationInSidebar.vue";

export default {
    components: {PlanUpgradeNotificationInSidebar},
    data() {
        return {
            options: {
                title: "",
                group: "",
                input: "to_convert/",
                output: "converted/",
            },
            exporting: false,
            tips: {
                requiredName: false,
            },
            worker: null
        };
    },

    validations: {
        options: {
            title: { required },
        }
    },

    created() {
        this.worker = new Worker("/assets/vendor/xmp/xmp_webconverter_worker.js");
        this.worker.onmessage = e => {
            this.save(e.data[0], e.data[1]);
        }
    },

    computed: {
        canEnterName() {
            if (this.action.action == "export-xmp-from-asset" && this.action.data.length > 1) return false;
            else return true;
        },
        assetType() {
            let type = this.action.type == "candidate" ? "Candidate" : "Match";
            return type;
        },
        ...mapState("Projects", ["action"]),
        ...mapState("UI", ["rightColumn"]),
        ...mapGetters('User', ['isCurrentPlanFree']),
    },

    watch: {
        action(newValue) {
            if (this.action.action == "export-xmp-from-asset" && this.action.data.length == 1) {
                this.options.title = newValue.data[0].nicename;
            }
        },

        rightColumn(newValue) {
            if (newValue == 'download-xmp') {
                this.$refs.name.focus();
            }
        }
    },

    methods: {
        hideEnterNameTip() {
            this.tips.requiredName = false;
        },
        async onSubmit() {
            if (this.$v.$invalid && this.canEnterName) {
                this.tips.requiredName = true;
                return;
            }

            this.tips.requiredName = false;
            this.exporting = true;

            if (this.options.title == "") this.options.title = "XMP-" + Date.now();

            let data = {
                name: this.options.title,
                options: {
                    ACESinput: true,
                    ACESoutput: true,
                    xmp: true,
                },
            }

            if (this.action.action == "export-xmp-from-asset") {
                await window.lutify.storeLastState();

                let exportXMPFromAssets = [];
                let selectedAssets = this.action.data;
                window.lutify.exportingLut = true; // to not switch between tabs on right

                let filesList = [];

                for await (const asset of selectedAssets) {
                    await window.lutify.applyCompare(asset.blocks, "waitToLoad", false);
                    let name = selectedAssets.length == 1 ? data.name : asset.nicename;
                    let lutFiles = await window.luts.prepareDownloadLuts({...data, name });
                    filesList.push(lutFiles[0]);
                }

                this.worker.postMessage([["to_convert/","converted/"], filesList, this.options]);

                await window.lutify.applyLastState(this.$store.state.Projects.image);

                window.lutify.exportingLut = false;

                await this.$store.dispatch("Projects/setAction", {
                    type: null,
                    action: "export-xmp-from-asset-complete",
                    data: null,
                });
            }

            // export for current image
            else {
                let lutFiles = await window.luts.prepareDownloadLuts(data);
                this.worker.postMessage([["to_convert/","converted/"], lutFiles, this.options]);
            }

            this.exporting = false;
            window.UI.hideModal();
            this.options.title = "";
            this.options.group = "";
        },

        save(filename, blob) {
            let elem = window.document.createElement('a');
            elem.href = window.URL.createObjectURL(blob);
            elem.download = filename;
            document.body.appendChild(elem);
            elem.click();
            document.body.removeChild(elem);
        },
    },
};
</script>

<style lang="scss">
#download-xmp {
    .switch-container {
        margin-left: initial;
    }

    label {
        //margin-bottom: 10px !important;
        line-height: 1;
        display: block;
        height: auto;
        padding: 0;
        font-size: 1rem;
    }

    .v-input {
        padding: 0;
        margin: 0;
    }

    .v-input__slot {
        padding: 0;
    }

    input[type="text"],
    textarea {
        background: white;
        -webkit-appearance: none;
        border-radius: 5px;
        border: none;
        color: var(--color-bg-dark);
        padding: 7px 10px;
        //margin-bottom: 20px;
        font-size: 1rem;

        &::placeholder {
            color: var(--font-default-color);
            font-family: Poppins;
            font-size: 1rem;
        }
    }

    textarea {
        resize: none;
        height: 80px;
    }

    input[type="submit"],
    button {
        margin: 0 auto;
        outline: 0;

        &:hover {
            opacity: 0.8;
        }
    }

    .option-container {
        display: flex;
        align-items: center;
        gap: 1rem;

        &.--disabled {
            opacity: 0.25;

            input {
                pointer-events: none;
                cursor: not-allowed;
            }
        }

        .option-title {
            line-height: 1.25;
        }
    }

    .btn-advanced-options {
        margin-bottom: 0;

        > span {
            line-height: 20px;
        }
    }

    textarea,
    input {
        line-height: 20px;
    }

    textarea {
        width: 100%;
        margin-bottom: 12px;
    }

    .description-container {
        margin-bottom: 8px;

        .v-expansion-panel-header {
            span {
                line-height: 12px;
            }
        }
    }

    div[name="export-lut-name-container"] {
        input {
            background: white;
            border-radius: 5px;
            border: none;
            color: #121212;
            padding: 7px 10px;
            font-size: 1rem;
            width: 100%;

            &:disabled {
                opacity: 0.3;
                cursor: not-allowed;
            }
        }
    }

    .buttons-container {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }

    .v-expansion-panel {
        background: transparent;
        color: inherit;

        &:before {
            display: none;
        }

        .v-expansion-panel-header {
            padding: 0;
            min-height: auto;
            flex-direction: row-reverse;
            margin-bottom: 10px;

            &:before {
                display: none;
            }

            .v-expansion-panel-header__icon i {
                font-size: 18px;
                color: var(--font-default-color);
            }
        }

        .v-expansion-panel-content__wrap {
            padding: 0;
        }
    }
}
</style>